/**
 * Offline DEMO dataset.
 *
 * The gateway is the source of truth; this module exists so the site still
 * renders a complete, honest page when the gateway (or Strapi behind it) is
 * unreachable — see data/gateway.ts, which flips to DEMO and raises the banner.
 *
 * The content is NOT invented here: it is projected from @mb-group/content-seed,
 * the same taxonomy the Strapi seed loads. DEMO and LIVE therefore show the same
 * catalogue, and there is exactly one place to edit it.
 */
import type { Locale } from "@mb-group/shared-types";
import {
	contentSeed,
	flatServices,
	type CaseSeed,
	type ContentSeed,
	type ServiceSeed,
} from "@mb-group/content-seed";
import { buildHref } from "../core/routes.js";
import { getCopy } from "../core/copy.js";
import type {
	Article,
	CaseStudy,
	Company,
	Footer,
	FormDefinition,
	MediaAsset,
	Navigation,
	NavigationItem,
	Page,
	Resource,
	SeoMeta,
	Service,
	ServiceCategory,
	Webinar,
} from "./types.js";

export interface DemoData {
	categories: ServiceCategory[];
	services: Service[];
	companies: Company[];
	caseStudies: CaseStudy[];
	articles: Article[];
	resources: Resource[];
	webinars: Webinar[];
	navigation: Navigation;
	footer: Footer;
	forms: Record<string, FormDefinition>;
	pages: Record<string, Page>;
}

const cache = new Map<Locale, DemoData>();

/** Build (and memoise) the DEMO dataset for a locale. */
export function buildDemo(locale: Locale): DemoData {
	const cached = cache.get(locale);
	if (cached) return cached;
	const built = build(locale);
	cache.set(locale, built);
	return built;
}

// --- helpers ----------------------------------------------------------------

function seo(title: string, description: string): SeoMeta {
	return {
		title,
		description,
		canonicalUrl: null,
		ogImage: null,
		noindex: false,
		hreflang: [],
		structuredData: null,
	};
}

function photo(basename: string, alt: string): MediaAsset {
	return {
		id: `media-${basename}`,
		key: `media/${basename}.webp`,
		url: `/static/${basename}.webp`,
		signedUrl: null,
		mime: "image/webp",
		sizeBytes: 0,
		width: 1400,
		height: 933,
		alt,
		isPrivate: false,
	};
}

/** Every publishable in DEMO mode carries isDemo so views can label it. */
function publishable(id: string, slug: string, locale: Locale) {
	return {
		id,
		documentId: id,
		slug,
		status: "published" as const,
		locale,
		isDemo: true,
		publishedAt: null,
	};
}

// --- builders ---------------------------------------------------------------

function build(locale: Locale): DemoData {
	const seed = contentSeed(locale);

	// Categories and services reference each other, so build the shells first and
	// wire the relations afterwards (avoids a circular construction order).
	const categories: ServiceCategory[] = seed.categories.map((c, i) => ({
		...publishable(`cat-${c.id}`, c.slug, locale),
		title: c.title,
		summary: c.summary,
		icon: c.num,
		order: i + 1,
		services: [],
	}));
	const categoryById = new Map(seed.categories.map((c, i) => [c.id, categories[i]!]));

	const services: Service[] = [];
	const serviceById = new Map<string, Service>();

	for (const { service: s, category: c } of flatServices(seed)) {
		const built: Service = {
			...publishable(`svc-${s.id}`, s.slug, locale),
			title: s.name,
			usp: s.short,
			summary: s.desc,
			body: serviceBody(s),
			heroImage: photo(servicePhoto(s.id), s.name),
			category: categoryById.get(c.id) ?? null,
			companies: [],
			caseStudies: [],
			materialStreams: s.groups,
			seo: seo(s.name, s.short),
		};
		services.push(built);
		serviceById.set(s.id, built);
		categoryById.get(c.id)?.services.push(built);
	}

	const caseStudies: CaseStudy[] = seed.cases.map((k) => buildCase(k, locale, serviceById));
	// Back-reference: a service detail page lists the case studies that used it.
	for (const study of caseStudies) {
		// The DEMO projection always fills `services` in; `?? []` is only here because
		// the shared type now admits absence for LIVE responses.
		for (const svc of study.services ?? []) svc.caseStudies.push(study);
	}

	const articles: Article[] = seed.insights.map((a) => ({
		...publishable(`art-${a.slug}`, a.slug, locale),
		title: a.title,
		excerpt: a.excerpt,
		body: articleBody(locale),
		heroImage: photo("rozmowy", a.title),
		authorName: null,
		tags: [a.tag],
		readingMinutes: a.readingMinutes,
		seo: seo(a.title, a.excerpt),
	}));

	const resources: Resource[] = seed.magnets.map((m) => ({
		...publishable(`res-${m.slug}`, m.slug, locale),
		title: m.title,
		description: m.desc,
		gated: true,
		kind: m.kind,
		file: null,
		seo: seo(m.title, m.desc),
	}));

	// One webinar mirrors the webinar-kind lead magnet so /webinary is not empty.
	const webinarMagnet = seed.magnets.find((m) => m.kind.toLowerCase().startsWith("webinar"));
	const webinars: Webinar[] = webinarMagnet
		? [
				{
					...publishable(`web-${webinarMagnet.slug}`, webinarMagnet.slug, locale),
					title: webinarMagnet.title,
					description: webinarMagnet.desc,
					// A fixed past date: the recording is on demand, not an upcoming event.
					startsAt: "2026-05-14T09:00:00.000Z",
					endsAt: "2026-05-14T10:00:00.000Z",
					registrationForm: null,
					recording: null,
					seo: seo(webinarMagnet.title, webinarMagnet.desc),
				},
			]
		: [];

	return {
		categories,
		services,
		companies: [],
		caseStudies,
		articles,
		resources,
		webinars,
		navigation: buildNavigation(locale, seed),
		footer: buildFooter(locale, seed),
		forms: buildForms(locale),
		pages: {},
	};
}

function buildCase(k: CaseSeed, locale: Locale, serviceById: Map<string, Service>): CaseStudy {
	const linked = k.serviceIds
		.map((id) => serviceById.get(id))
		.filter((s): s is Service => s !== undefined);
	return {
		...publishable(`case-${k.slug}`, k.slug, locale),
		title: k.title,
		tag: k.tag,
		challenge: k.challenge,
		solution: k.solution,
		outcome: k.outcome,
		metrics: [{ label: k.statLabel, value: k.stat }],
		heroImage: photo(k.photo, k.title),
		services: linked,
		company: null,
		seo: seo(k.title, k.outcome),
	};
}

/**
 * The detail-page body, as trusted rich text: the lead paragraph followed by the
 * "what exactly we deliver" scope list. The schema has no dedicated scope field,
 * so the list travels inside `body` as a <ul> — the same shape the Strapi seed
 * writes, which is why views/services.ts can extract it from LIVE content too.
 */
function serviceBody(s: ServiceSeed): string {
	const items = s.spec.map((item) => `<li>${escapeHtml(item)}</li>`).join("");
	return `<p>${escapeHtml(s.desc)}</p><ul>${items}</ul>`;
}

function articleBody(locale: Locale): string {
	const paragraphs =
		locale === "en"
			? [
					"The full text of this analysis is prepared by the team that owns the relevant service category. This page shows the layout: heading, lead, visual, body copy and a conversion block at the end.",
					"Every analysis closes with an offer of a downloadable material or a conversation with the category manager — the main lead-capture mechanism in this section of the site.",
				]
			: [
					"Treść tej analizy przygotowuje zespół odpowiedzialny za daną kategorię usług. Ta strona pokazuje układ: nagłówek, lead, materiał wizualny, tekst oraz blok konwersji na końcu.",
					"Każda analiza kończy się propozycją materiału do pobrania lub kontaktu z opiekunem kategorii — to główny mechanizm zbierania leadów w tej sekcji serwisu.",
				];
	return paragraphs.map((p) => `<p>${escapeHtml(p)}</p>`).join("");
}

function escapeHtml(value: string): string {
	return value
		.replace(/&/g, "&amp;")
		.replace(/</g, "&lt;")
		.replace(/>/g, "&gt;")
		.replace(/"/g, "&quot;");
}

/**
 * Photo assignment for service heroes. The mock-up cycles the trade-fair photo
 * set deterministically, so the same service always shows the same image.
 */
const SERVICE_PHOTOS = [
	"rozmowy",
	"handshake",
	"stoisko-tlum",
	"team-stoisko",
	"p5",
	"p6",
	"p7",
	"p8",
] as const;

function servicePhoto(serviceId: string): string {
	let hash = 0;
	for (let i = 0; i < serviceId.length; i += 1) hash = (hash * 31 + serviceId.charCodeAt(i)) | 0;
	const index = Math.abs(hash) % SERVICE_PHOTOS.length;
	return SERVICE_PHOTOS[index] ?? "rozmowy";
}

function navItem(
	label: string,
	routeKey: NavigationItem["routeKey"],
	href: string,
	children?: NavigationItem[],
): NavigationItem {
	return children ? { label, routeKey, href, children } : { label, routeKey, href };
}

function buildNavigation(locale: Locale, seed: ContentSeed): Navigation {
	const copy = getCopy(locale);
	const href = (key: Parameters<typeof buildHref>[0], params?: Record<string, string>) =>
		buildHref(key, locale, params);

	return {
		id: "nav-demo",
		primary: [
			navItem(
				copy.nav.services,
				"services",
				href("services"),
				seed.categories.map((c) =>
					navItem(c.title, "services", `${href("services")}#kategoria-${c.slug}`),
				),
			),
			navItem(copy.nav.industries, "industries", href("industries")),
			navItem(copy.nav.cases, "caseStudies", href("caseStudies")),
			navItem(copy.nav.knowledge, "knowledge", href("knowledge")),
			navItem(copy.nav.about, "about", href("about")),
		],
		utility: [navItem(copy.nav.contact, "contact", href("contact"))],
	};
}

function buildFooter(locale: Locale, seed: ContentSeed): Footer {
	const copy = getCopy(locale);
	const href = (key: Parameters<typeof buildHref>[0]) => buildHref(key, locale);

	return {
		id: "footer-demo",
		columns: [
			{
				heading: copy.footer.servicesTitle,
				items: seed.categories.map((c) =>
					navItem(c.title, "services", `${href("services")}#kategoria-${c.slug}`),
				),
			},
			{
				heading: copy.footer.groupTitle,
				items: [
					navItem(copy.nav.about, "about", href("about")),
					navItem(copy.nav.industries, "industries", href("industries")),
					navItem(copy.nav.cases, "caseStudies", href("caseStudies")),
					navItem(copy.nav.knowledge, "knowledge", href("knowledge")),
					navItem(copy.nav.contact, "contact", href("contact")),
				],
			},
		],
		legalLinks: [
			navItem(
				locale === "en" ? "Privacy policy" : "Polityka prywatności",
				"privacy",
				href("privacy"),
			),
			navItem("Cookies", "cookies", href("cookies")),
		],
		copyright: `© 2026 MB Group. ${copy.footer.rights}`,
	};
}

function buildForms(locale: Locale): Record<string, FormDefinition> {
	const copy = getCopy(locale);
	const seedData = contentSeed(locale);

	const contact: FormDefinition = {
		id: "form-contact-demo",
		key: "contact",
		title: copy.contact.formTitle,
		fields: [
			{ name: "name", label: copy.contact.name, type: "text", required: true },
			{ name: "company", label: copy.contact.company, type: "text", required: false },
			{ name: "email", label: copy.contact.email, type: "email", required: true },
			{
				name: "category",
				label: copy.contact.categoryPlaceholder,
				type: "select",
				required: false,
				options: seedData.categories.map((c) => ({ label: c.title, value: c.slug })),
			},
			{ name: "message", label: copy.contact.message, type: "textarea", required: true },
		],
		consents: [],
		submitLabel: copy.contact.submit,
		successMessage: copy.contact.sentDesc,
		leadSource: "contact_form",
	};

	const callback: FormDefinition = {
		id: "form-callback-demo",
		key: "callback",
		title: copy.quickForm.title,
		fields: [
			{ name: "name", label: copy.quickForm.name, type: "text", required: true },
			{ name: "phone", label: copy.quickForm.phone, type: "tel", required: true },
			{
				name: "slot",
				label: copy.quickForm.slotLabel,
				type: "select",
				required: false,
				options: copy.quickForm.slots.map((s, i) => ({ label: s, value: String(i) })),
			},
		],
		consents: [],
		submitLabel: copy.quickForm.submit,
		successMessage: copy.quickForm.sentDesc,
		leadSource: "contact_form",
	};

	const newsletter: FormDefinition = {
		id: "form-newsletter-demo",
		key: "newsletter",
		title: copy.newsletter.title,
		fields: [
			{ name: "email", label: copy.newsletter.emailPlaceholder, type: "email", required: true },
		],
		consents: [],
		submitLabel: copy.newsletter.submit,
		successMessage: copy.newsletter.doneDesc,
		leadSource: "newsletter",
	};

	return { contact, callback, newsletter };
}
